/**
 * Cliente WebRTC da Realtime API da OpenAI.
 *
 * Fluxo:
 *   1. `POST /api/realtime/session` no nosso backend devolve um client secret
 *      efêmero, já com a sessão configurada pela política publicada.
 *   2. Criamos a RTCPeerConnection, adicionamos o microfone e um data channel
 *      `oai-events` para os eventos de sessão.
 *   3. Trocamos SDP com `POST https://api.openai.com/v1/realtime/calls`
 *      usando *só* o segredo efêmero.
 *
 * A chave permanente nunca chega ao navegador.
 */

import { api } from './api'
import type { Mark } from './types'

export interface RealtimeCallbacks {
  /** Delta de transcrição. `speaker` distingue a fala da IA da fala do cliente. */
  onTranscriptDelta(speaker: 'ai' | 'cli', delta: string): void
  /** Turno concluído — hora de fechar o balão. */
  onTurnDone(speaker: 'ai' | 'cli'): void
  /** O modelo chamou uma ferramenta. Devolva o resultado para o modelo seguir. */
  onToolCall(name: string, args: Record<string, unknown>, callId: string): Promise<unknown>
  /** Mudança de estado do orb: quem está falando. */
  onSpeakingChange(state: 'idle' | 'ai' | 'cli' | 'think'): void
  /** Fluxo de áudio remoto disponível — usado para o analisador de amplitude. */
  onRemoteStream(stream: MediaStream): void
  /** Eventos dignos de trilha de auditoria. */
  onAudit(event: string, marks?: Mark[]): void
  onError(error: Error): void
  onClose(): void
}

export interface RealtimeHandle {
  close(): void
  /** Injeta uma instrução do supervisor sem que o cliente ouça. */
  whisper(text: string): void
  setMuted(muted: boolean): void
  /**
   * Assumir/devolver a chamada.
   *
   * Pausar de verdade exige duas coisas: cancelar a resposta em curso (senão a
   * IA termina a frase por cima do operador) e desligar a detecção de turno
   * (senão ela volta a responder no próximo silêncio).
   */
  setAgentPaused(paused: boolean): void
  readonly model: string
  readonly voice: string
}

interface RealtimeEvent {
  type: string
  delta?: string
  transcript?: string
  name?: string
  arguments?: string
  call_id?: string
  item_id?: string
  error?: { message?: string; type?: string }
  [key: string]: unknown
}

export async function connectRealtime(
  callbacks: RealtimeCallbacks,
  opts: { caseId?: string } = {},
): Promise<RealtimeHandle> {
  const session = await api.realtimeSession(opts.caseId)

  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  })

  // Áudio remoto (a voz da Alice).
  const audio = new Audio()
  audio.autoplay = true
  pc.ontrack = (event) => {
    const [stream] = event.streams
    audio.srcObject = stream
    callbacks.onRemoteStream(stream)
  }

  // Microfone da sala.
  const mic = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  })
  for (const track of mic.getTracks()) pc.addTrack(track, mic)

  const channel = pc.createDataChannel('oai-events')

  const send = (payload: Record<string, unknown>) => {
    if (channel.readyState === 'open') channel.send(JSON.stringify(payload))
  }

  channel.addEventListener('message', (event) => {
    let parsed: RealtimeEvent
    try {
      parsed = JSON.parse(event.data as string)
    } catch {
      return
    }
    void handleEvent(parsed, callbacks, send)
  })

  channel.addEventListener('close', () => callbacks.onClose())

  pc.addEventListener('connectionstatechange', () => {
    if (pc.connectionState === 'failed') {
      callbacks.onError(new Error('Conexão WebRTC falhou'))
    }
    if (pc.connectionState === 'closed') callbacks.onClose()
  })

  // Troca de SDP.
  const offer = await pc.createOffer()
  await pc.setLocalDescription(offer)

  const answer = await fetch(session.calls_url, {
    method: 'POST',
    body: offer.sdp,
    headers: {
      Authorization: `Bearer ${session.client_secret}`,
      'Content-Type': 'application/sdp',
    },
  })

  if (!answer.ok) {
    pc.close()
    for (const track of mic.getTracks()) track.stop()
    throw new Error(
      `Realtime API recusou o SDP (${answer.status}): ${(await answer.text()).slice(0, 200)}`,
    )
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: await answer.text() })
  callbacks.onSpeakingChange('think')

  return {
    model: session.model,
    voice: session.voice,
    close() {
      try {
        channel.close()
      } catch {
        /* já fechado */
      }
      for (const track of mic.getTracks()) track.stop()
      pc.close()
      audio.srcObject = null
    },
    whisper(text: string) {
      // Instrução do supervisor: entra como mensagem de sistema, não como fala
      // do cliente, e por isso não aparece na transcrição dele.
      send({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text: `Orientação do supervisor: ${text}` }],
        },
      })
      send({ type: 'response.create' })
    },
    setMuted(muted: boolean) {
      for (const track of mic.getAudioTracks()) track.enabled = !muted
    },
    setAgentPaused(paused: boolean) {
      if (paused) {
        send({ type: 'response.cancel' })
        send({ type: 'session.update', session: { audio: { input: { turn_detection: null } } } })
      } else {
        send({
          type: 'session.update',
          session: {
            audio: {
              input: {
                turn_detection: { type: 'semantic_vad', interrupt_response: true },
              },
            },
          },
        })
      }
    },
  }
}

async function handleEvent(
  event: RealtimeEvent,
  callbacks: RealtimeCallbacks,
  send: (payload: Record<string, unknown>) => void,
): Promise<void> {
  switch (event.type) {
    // --- fala da IA -------------------------------------------------------
    case 'response.output_audio_transcript.delta':
    case 'response.audio_transcript.delta':
      callbacks.onSpeakingChange('ai')
      callbacks.onTranscriptDelta('ai', event.delta ?? '')
      break

    case 'response.output_audio_transcript.done':
    case 'response.audio_transcript.done':
      callbacks.onTurnDone('ai')
      break

    // --- fala do cliente --------------------------------------------------
    case 'conversation.item.input_audio_transcription.delta':
      callbacks.onSpeakingChange('cli')
      callbacks.onTranscriptDelta('cli', event.delta ?? '')
      break

    case 'conversation.item.input_audio_transcription.completed':
      callbacks.onTurnDone('cli')
      callbacks.onSpeakingChange('think')
      break

    // --- barge-in ---------------------------------------------------------
    case 'input_audio_buffer.speech_started':
      callbacks.onSpeakingChange('cli')
      callbacks.onAudit('Barge-in: cliente interrompeu · IA parou de falar')
      break

    case 'response.done':
      callbacks.onSpeakingChange('think')
      break

    // --- ferramentas ------------------------------------------------------
    case 'response.function_call_arguments.done': {
      const name = event.name ?? ''
      const callId = event.call_id ?? event.item_id ?? ''
      let args: Record<string, unknown> = {}
      try {
        args = JSON.parse(event.arguments || '{}')
      } catch {
        callbacks.onError(new Error(`Argumentos inválidos em ${name}`))
      }

      try {
        const result = await callbacks.onToolCall(name, args, callId)
        // Devolve o resultado ao modelo para que ele siga a conversa. Sem isso
        // a IA fica esperando e a chamada trava.
        send({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(result ?? { ok: true }),
          },
        })
        send({ type: 'response.create' })
      } catch (error) {
        send({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify({
              ok: false,
              error: error instanceof Error ? error.message : 'falha ao executar ferramenta',
            }),
          },
        })
        send({ type: 'response.create' })
      }
      break
    }

    case 'error':
      callbacks.onError(new Error(event.error?.message ?? 'Erro da Realtime API'))
      break

    default:
      break
  }
}

/**
 * Analisador de amplitude para animar o orb com o áudio real.
 * Devolve uma função que lê o nível atual (0–1) e outra para desligar.
 */
export function createAmplitudeAnalyser(stream: MediaStream) {
  const AudioCtor = window.AudioContext ?? (window as any).webkitAudioContext
  const context: AudioContext = new AudioCtor()
  const analyser = context.createAnalyser()
  analyser.fftSize = 512
  context.createMediaStreamSource(stream).connect(analyser)
  const buffer = new Uint8Array(analyser.frequencyBinCount)

  return {
    read(): number {
      analyser.getByteTimeDomainData(buffer)
      let sum = 0
      for (let i = 0; i < buffer.length; i += 1) {
        const value = (buffer[i] - 128) / 128
        sum += value * value
      }
      return Math.min(1, Math.sqrt(sum / buffer.length) * 4.5)
    },
    dispose() {
      void context.close()
    },
  }
}
